import React from 'react'
import {
	BrowserRouter as Router,
	Route,
	Routes,
	useLocation,
} from 'react-router-dom'

// CONTEXTS
import { AuthProvider, useAuth } from './context/AuthContext'
import { ServerProvider, useServer } from './context/ServerContext'

import Profile from './components/Profile'
import ServerStatsComponent from './components/ServerStats'
import TopPlayers from './components/TopPlayers'
import CharacterDetails from './components/CharacterDetails'

// COMPONENTS
import Header from './components/Header'
import LoginFormComponent from './components/LoginForm'
import SliderComponent from './components/Slider'
import NewsComponent from './components/News'
import Loader from './components/Loader'
import Register from './components/Register'
import Downloads from './components/Downloads'
import Rankings from './components/Rankings'
import Events from './components/Events'

import backgroundImage from './assets/images/bg_3_xs.webp'
import { useEffect, useRef } from 'react'

const MainPage: React.FC = () => {
	const { loading: authLoading } = useAuth()
	const { loading: serverLoading } = useServer()
	const location = useLocation() // Получаем текущий маршрут
	const dynamicComponentRef = useRef<HTMLDivElement>(null)

	// Показываем лоадер, если хотя бы один контекст загружается
	const isLoading = authLoading || serverLoading

	// Прокручиваем к динамическому компоненту при изменении маршрута
	useEffect(() => {
		if (
			!isLoading &&
			dynamicComponentRef.current &&
			location.pathname !== '/'
		) {
			dynamicComponentRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			})
		}
	}, [location.pathname, isLoading]) // Зависимость от маршрута и состояния загрузки

	if (isLoading) {
		return <Loader />
	}

	// Определяем, какой компонент отображать вместо NewsComponent
	const renderDynamicComponent = () => {
		switch (location.pathname) {
			case '/':
				return <NewsComponent />
			case '/register':
				return <Register />
			case '/downloads':
				return <Downloads />
			case '/rankings':
				return <Rankings />
			case '/events':
				return <Events />
			default:
				return null // Если маршрут не совпадает, ничего не отображаем
		}
	}

	return (
		<div
			className='min-h-screen text-blue-900 bg-cover bg-fixed'
			style={{
				backgroundImage: `url(${backgroundImage})`,
			}}
		>
			<Header />
			{/* Основной контент */}
			<div className='container max-w-screen-xl mx-auto py-8 grid md:grid-cols-4 gap-2'>
				{/* Основной контент: статус сервера и рейтинги */}
				<SliderComponent />
				{/* Форма входа */}
				<LoginFormComponent />
				{/* Динамически отображаем компонент */}
				<div
					ref={dynamicComponentRef}
					className='bg-sky-50 animate-slide-up md:col-span-3 row-span-2 p-4'
				>
					{renderDynamicComponent()}
				</div>
				{/* Статус сервера */}
				<ServerStatsComponent />
				{/* Рейтинги */}
				<TopPlayers />
			</div>

			{/* Футер */}
			<footer className='bg-gray-800 p-4 text-center text-gray-400 mt-8'>
				<p>© 2025 MuOnline Server. All rights reserved.</p>
			</footer>
		</div>
	)
}
const App: React.FC = () => {
	return (
		<AuthProvider>
			<ServerProvider>
				<Router>
					<Routes>
						<Route path='/' element={<MainPage />} />
						<Route path='/register' element={<MainPage />} />
						<Route path='/downloads' element={<MainPage />} />
						<Route path='/rankings' element={<MainPage />} />
						<Route path='/events' element={<MainPage />} />
						<Route path='/dashboard' element={<Profile />} />
						<Route path='/character/:name' element={<CharacterDetails />} />
					</Routes>
				</Router>
			</ServerProvider>
		</AuthProvider>
	)
}

export default App
